import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ACCESS_LABEL,
  fmtDateTime,
  initials,
  navSections,
  visibleNav,
  type NavItem,
  type Notification,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { EmptyState } from './EmptyState';
import { ErrorBoundary } from './ErrorBoundary';
import { Modal } from './Modal';
import { LoadingBlock } from './Spinner';

function NavList({
  items,
  counts,
}: {
  items: readonly NavItem[];
  counts: Readonly<Record<string, number>>;
}): ReactNode {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.key}
          to={`/${item.key}`}
          className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
        >
          <span className="ic" aria-hidden="true">
            {item.icon}
          </span>
          {item.label}
          {/* Zero is not shown: an empty queue needs no badge, and a row of
              grey zeros trains people to ignore the number that matters. */}
          {counts[item.key] ? (
            <span className="count" aria-label={`${counts[item.key]} waiting`}>
              {counts[item.key]}
            </span>
          ) : null}
        </NavLink>
      ))}
    </>
  );
}

function NotificationsModal({ onClose }: { onClose: () => void }): ReactNode {
  const [items, setItems] = useState<Notification[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.notifications.list<Notification[]>();
        if (!cancelled) setItems(list);
        await api.notifications.markRead<null>();
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Modal title="Notifications" onClose={onClose}>
      {error ? <div className="auth-error">{error}</div> : null}
      {items === null && !error ? <LoadingBlock /> : null}
      {items !== null && items.length === 0 ? (
        <EmptyState icon="🔔" title="No notifications yet." />
      ) : null}
      {items?.map((n) => (
        <div
          key={n.id}
          style={{ padding: '10px 0', borderBottom: '1px solid #EEF2F6', fontSize: 12.5 }}
        >
          <div>{n.text}</div>
          <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
            {fmtDateTime(n.ts)}
          </div>
        </div>
      ))}
    </Modal>
  );
}

export function AppShell(): ReactNode {
  const user = useCurrentUser();
  const { signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [counts, setCounts] = useState<Readonly<Record<string, number>>>({});
  const [showNotifs, setShowNotifs] = useState(false);
  const [unread, setUnread] = useState(0);

  // Navigating on a phone should dismiss the off-canvas sidebar behind you.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.notifications.list<Notification[]>();
        if (!cancelled) setUnread(list.filter((n) => !n.read).length);
      } catch {
        /* The bell badge is not worth interrupting the page for. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const nav = visibleNav(user);
  const sections = navSections(user);
  const activeKey = location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  const current = nav.find((n) => n.key === activeKey);
  const heading = current?.label ?? 'Dashboard';
  // The section above the title, so a page says where it sits without the
  // sidebar having to be read again.
  const eyebrow = current?.group || 'Overview';

  /**
   * Queue depth, refreshed whenever the route changes.
   *
   * Acting on a claim is what changes these numbers, and acting on a claim
   * always ends in a navigation — so there is no polling, and the badge is
   * never more than one page-view stale. A failure is silent: a missing badge
   * is a smaller problem than an error banner over the whole app.
   */
  useEffect(() => {
    let cancelled = false;
    void api.counts
      .queues<Record<string, number>>()
      .then((next) => {
        if (!cancelled) setCounts(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const openNotifs = useCallback(() => {
    setShowNotifs(true);
    setUnread(0);
  }, []);

  return (
    <div className="shell">
      <button
        type="button"
        aria-label="Close menu"
        className={sidebarOpen ? 'sidebar-scrim open' : 'sidebar-scrim'}
        onClick={() => setSidebarOpen(false)}
      />
      <nav className={sidebarOpen ? 'sidebar open' : 'sidebar'} aria-label="Main">
        <div className="brand">
          {/* On a white plate deliberately. The mark is transparent and its teal
              has almost no contrast against navy chrome — on the bare sidebar
              the blade of the logo disappears entirely. */}
          <img src="/logo.svg" alt="" />
          <div>
            <b>GDEC Expense Claims</b>
            <span>{ACCESS_LABEL[user.systemAccess]}</span>
          </div>
        </div>
        <div className="nav-group">
          {sections.map((section) => (
            <div className="nav-sec" key={section.group || 'top'}>
              {section.group ? (
                <div className="nav-sec-label">
                  {section.group}
                  {section.restricted ? <span className="tag">Restricted</span> : null}
                </div>
              ) : null}
              <NavList items={section.items} counts={counts} />
            </div>
          ))}
        </div>
        <div className="sidebar-foot">
          GDEC Expense Claim
          <br />
          Management System
        </div>
      </nav>

      <div className="main">
        <div className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="menu-toggle"
              aria-label="Open menu"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen((open) => !open)}
            >
              ☰
            </button>
            <div>
              <div className="crumb">{eyebrow}</div>
              <h1>{heading}</h1>
            </div>
          </div>
          <div className="topbar-right">
            <button
              type="button"
              className="bell"
              onClick={openNotifs}
              aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
            >
              🔔
              {unread > 0 ? <span className="dot" /> : null}
            </button>
            <div className="who-chip">
              <div className="avatar" aria-hidden="true">
                {initials(user.name)}
              </div>
              <div className="meta">
                <b>{user.name}</b>
                <span>
                  {user.department} · {ACCESS_LABEL[user.systemAccess]}
                </span>
              </div>
            </div>
            <button type="button" className="logout-btn" onClick={signOut}>
              Logout
            </button>
          </div>
        </div>

        <div className="content">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>

      {showNotifs ? <NotificationsModal onClose={() => setShowNotifs(false)} /> : null}
    </div>
  );
}
